import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    SimpleChange,
    ViewChild,
} from '@angular/core'
import {
    CalendarDayAvailablePartner,
    CalendarDayComponent,
    CalendarDayData,
    TIMESLOT_HEIGHT,
} from '../calendar-day/calendar-day.component'
import { TimeslotLabelsComponent } from '../timeslot-labels/timeslot-labels.component'
import { CommonModule } from '@angular/common'
import {
    AvailableTimesAndPartnersLists,
    AvailableTimesService,
} from '@app/core/_services/available-times.service'
import { Subject, takeUntil } from 'rxjs'
import { MS_IN_ONE_DAY } from '@app/core/_services/time-utilities.service'
import {
    ConfirmedSessionsResponse,
    Container,
    SESSION_DURATION_25_MINS,
    SESSION_DURATION_50_MINS,
    SESSION_DURATION_75_MINS,
    TimeFormat,
} from '@app/core/_models'
import {
    ConfirmedSessionsService,
    SelectedSession,
    SelectedSessionService,
    UtilsService,
} from '@app/core/_services'
import { EpochToTimePipe } from '@app/core/pipes/epoch-to-time.pipe'
import { DayData } from '../calendar/calendar.component'
import { DomSanitizer, SafeStyle } from '@angular/platform-browser'
import { ContentStackService } from '@app/core/_services/content-stack.service'

@Component({
    selector: 'fm-calendar-days',
    imports: [CalendarDayComponent, TimeslotLabelsComponent, CommonModule, EpochToTimePipe],
    templateUrl: './calendar-days.component.html',
    styleUrl: './calendar-days.component.scss',
})
export class CalendarDaysComponent {
    //@ViewChild('scrollPanel') scrollPanel!: ScrollPanel
    @ViewChild('scrollPanel', { read: ElementRef, static: true }) scrollPanel: ElementRef

    @Input() days: Array<DayData> = new Array<DayData>(0)
    @Input() timezone: string
    @Input() timeFormat: TimeFormat = '12hr'
    @Input() sessionDuration: number
    @Output() updatedScrollbarWidth = new EventEmitter<number>()
    public selectedSessionData: SelectedSession[]
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    private availableTimesAndPartnersFullList: AvailableTimesAndPartnersLists
    private confirmedSessionsFullList: Container<ConfirmedSessionsResponse>

    public calendarDayData: CalendarDayData[] = []

    // Temporary until we figure out ghosting outline issue
    // with PrimeNg Scroll panel
    public toolbarWidth: SafeStyle
    private scrollBarWidth: number = 15

    constructor(
        private sanitizer: DomSanitizer,
        private utilsService: UtilsService,
        private confirmedSessionService: ConfirmedSessionsService,
        private selectedSessionService: SelectedSessionService,
        private availableTimesService: AvailableTimesService,
        private contentStackService: ContentStackService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.availableTimesService.availableTimesAndPartners
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((availableTimesAndPartners: AvailableTimesAndPartnersLists) => {
                this.availableTimesAndPartnersFullList = availableTimesAndPartners
                this.buildDayData()
            })

        this.selectedSessionService.selectedSessions$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((selectedSessions: SelectedSession[]) => {
                this.selectedSessionData = [...selectedSessions]
                this.buildDayData()
            })

        this.confirmedSessionService.confirmedSessionsSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((confirmedSessions: Container<ConfirmedSessionsResponse>) => {
                this.confirmedSessionsFullList = confirmedSessions
                this.buildDayData()
            })

        this.contentStackService.panelToggled$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
            // Force re-render as the way widths will have changed but not
            // change detection would occur
            this.buildDayData()
        })
    }

    ngOnChanges(changes: SimpleChange) {
        this.buildDayData()
    }

    ngDoCheck() {
        this.scrollBarWidth =
            this.scrollPanel.nativeElement.getBoundingClientRect().width -
            this.scrollPanel.nativeElement.clientWidth
        this.setToolbarWidth()
    }

    ngAfterViewInit() {
        // Wait for the view to initialize to ensure the scrollPanel is available
        setTimeout(() => {
            if (this.scrollPanel) {
                // && this.scrollPanel.contentViewChild) {
                const now = this.utilsService.getCurrentTimeForTimezone(this.timezone)
                const hour = now.hour()
                // this.scrollPanel.contentViewChild.nativeElement.scrollTop =
                //     hour * TIMESLOT_HEIGHT * 4
                this.scrollPanel.nativeElement.scrollTop = hour * TIMESLOT_HEIGHT * 4
                this.setToolbarWidth()
            }
        })
    }

    private setToolbarWidth() {
        let thewidth: string = `calc(100% - ${this.scrollBarWidth}px)`
        this.toolbarWidth = this.sanitizer.bypassSecurityTrustStyle(thewidth)
        this.updatedScrollbarWidth.emit(this.scrollBarWidth)
    }

    private buildDayData() {
        const dayData = new Array<CalendarDayData>(0)
        const potentialPartnerDatas = this.populateDayAvailabilityData()
        const bookedSessionDatas = this.populateConfirmedSessionData()
        const selectedSessionDatas = this.populateSelectedSessionData()

        for (let dayIndex in this.days) {
            dayData.push({
                startOfDay: this.days[dayIndex].startOfDayInMs,
                potentialPartnerData: potentialPartnerDatas[dayIndex],
                bookedSessionData: bookedSessionDatas[dayIndex],
                selectedSessionData: selectedSessionDatas[dayIndex],
            })
        }
        this.calendarDayData = [...dayData]
    }

    private getSessionMapForDuration() {
        if (this.sessionDuration === SESSION_DURATION_25_MINS) {
            return this.availableTimesAndPartnersFullList.sessions25minutes
        } else if (this.sessionDuration === SESSION_DURATION_50_MINS) {
            return this.availableTimesAndPartnersFullList.sessions50minutes
        } else {
            return this.availableTimesAndPartnersFullList.sessions75minutes
        }
    }

    private populateDayAvailabilityData() {
        const calendarDayAvailablePartnersArray: CalendarDayAvailablePartner[][] = new Array(
            this.days.length,
        )
            .fill(null)
            .map(() => new Array<CalendarDayAvailablePartner>())

        if (this.availableTimesAndPartnersFullList) {
            const durationMap = this.getSessionMapForDuration()

            for (const [sessionTime, timeSlotData] of durationMap.entries()) {
                const dayIndeces = this.getDayIndexForSessionTime(sessionTime)
                for (let dayIndex of dayIndeces) {
                    calendarDayAvailablePartnersArray[dayIndex].push({
                        sessionTime: sessionTime,
                        sessionDuration: this.sessionDuration,
                        ...timeSlotData,
                    })
                }
            }
        }
        return calendarDayAvailablePartnersArray
    }

    private populateConfirmedSessionData() {
        const confirmedSessions: ConfirmedSessionsResponse[][] = new Array(this.days.length)
            .fill(null)
            .map(() => new Array<ConfirmedSessionsResponse>(0))

        if (this.confirmedSessionsFullList) {
            for (const sessionInfo of this.confirmedSessionsFullList.getArray()) {
                const dayIndeces = this.getDayIndexForSessionTime(sessionInfo.sessionTime)
                for (let dayIndex of dayIndeces) {
                    confirmedSessions[dayIndex].push(sessionInfo)
                }
            }
        }
        return confirmedSessions
    }

    private populateSelectedSessionData() {
        const selectedSessions: SelectedSession[][] = new Array(this.days.length)
            .fill(null)
            .map(() => new Array<SelectedSession>(0))

        if (this.selectedSessionData) {
            for (const sessionInfo of this.selectedSessionData) {
                const dayIndeces = this.getDayIndexForSessionTime(sessionInfo.sessionTime)
                for (let dayIndex of dayIndeces) {
                    selectedSessions[dayIndex].push(sessionInfo)
                }
            }
        }
        return selectedSessions
    }

    private getDayIndexForSessionTime(sessionTime: number): number[] {
        const indeces = []
        for (let i = 0; i < this.days.length; i++) {
            if (
                sessionTime >= this.days[i].startOfDayInMs - SESSION_DURATION_75_MINS &&
                sessionTime < this.days[i].startOfDayInMs + MS_IN_ONE_DAY + SESSION_DURATION_75_MINS
            ) {
                indeces.push(i)
            }
        }
        return indeces
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
